import type { Conversation } from '@/types/chat';

function finiteDateTime(value: Date | null | undefined): number | null {
  if (!value) return null;
  const time = value.getTime();
  return Number.isFinite(time) ? time : null;
}

/**
 * Mirrors the backend's personal conversation projection order.
 *
 * Active pins sort by a valid pinned timestamp, while archived rows retain pin
 * metadata without receiving priority. Invalid legacy dates sort below valid
 * dates and reach the deterministic id fallback instead of returning NaN.
 */
export function compareConversationProjectionOrder(
  left: Conversation,
  right: Conversation,
): number {
  const activePinnedAtTime = (conversation: Conversation): number | null => {
    if (conversation.isArchived) return null;
    return finiteDateTime(conversation.pinnedAt);
  };
  const leftPinTime = activePinnedAtTime(left);
  const rightPinTime = activePinnedAtTime(right);
  if (leftPinTime !== null && rightPinTime !== null && leftPinTime !== rightPinTime) {
    return rightPinTime - leftPinTime;
  }
  if (leftPinTime !== null && rightPinTime === null) return -1;
  if (leftPinTime === null && rightPinTime !== null) return 1;

  const leftActivityTime = finiteDateTime(left.lastMessageAt);
  const rightActivityTime = finiteDateTime(right.lastMessageAt);
  if (leftActivityTime !== null && rightActivityTime !== null && leftActivityTime !== rightActivityTime) {
    return rightActivityTime - leftActivityTime;
  }
  if (leftActivityTime !== null && rightActivityTime === null) return -1;
  if (leftActivityTime === null && rightActivityTime !== null) return 1;
  return right.id.localeCompare(left.id);
}
